import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import config from './config';
import { baseUrl, gatewayUrl, demoUrl } from './base';
import { isServer } from 'utils/helper';
import * as Sentry from '@sentry/react';
import { getSavedFingerprintID } from 'utils/fingerprint';

interface Res {
  code: number;
  msg: string;
}

type ResState<T> = {
  [P in keyof T]: T[P];
} & Res;

const isProd = process.env.NODE_ENV === 'production';

class Request {
  instance: AxiosInstance;

  constructor(config: AxiosRequestConfig) {
    this.instance = axios.create(config);

    this.instance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        if (isServer()) {
          if (config.headers?.isDemoTrading) {
            config.url = config.url?.replace(baseUrl, demoUrl);
          } else if (isProd) {
            config.url = config.url?.replace(baseUrl, gatewayUrl);
          }
        }

        // 目前 requestId 的获取仅针对客户端请求有效
        // 如果需要在服务端请求中使用 requestId
        // 需要业务放在服务端请求时单独进行处理
        const requestId = getSavedFingerprintID();
        if (requestId) {
          config.headers = {
            ...config.headers,
            'fp-request-id': requestId,
          };
        }

        return config;
      },
      (error) => {
        return Promise.reject(JSON.stringify(error.message));
      }
    );

    this.instance.interceptors.response.use(
      (res: AxiosResponse) => {
        if (res?.data?.code === 500 || res.status >= 500) {
          try {
            Sentry.captureMessage(
              `statusCode: ${res.status} for ${res?.request?.responseURL}, statusText: ${res.statusText}, data code: ${res?.data?.code} message: ${res?.data?.msg}`
            );
          } catch (err) {
            console.log(err);
          }
        }

        return res.data;
      },
      (error: AxiosError) => {
        return Promise.reject(JSON.stringify(error.message));
      }
    );
  }

  request(config: AxiosRequestConfig) {
    return this.instance.request(config);
  }

  get<T>(url: string, params?: object, config?: object): Promise<ResState<T>> {
    return this.instance.get(url, { params, ...config });
  }

  post<T>(url: string, params?: object, config?: object): Promise<ResState<T>> {
    return this.instance.post(url, params, config);
  }
}

export default new Request(config);
